import React, { useEffect, useState } from "react";
import DateRange from "carbon-react/lib/__experimental__/components/date-range";
import { isValidISODateString } from "../../constants/validation";

const carbonDateSeparator = "-";

const getDateTimePartsFromRawCarbonDateTime = (carbonDate) => {
  const parts = carbonDate.split(carbonDateSeparator);
  if (!parts || parts.length !== 3)
    throw new Error(`Invalid carbon date format: '${carbonDate}'`);
  return [parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])];
};

const parseIsoStringFromCarbonFromDate = (carbonDate) => {
  const parts = getDateTimePartsFromRawCarbonDateTime(carbonDate);
  return new Date(parts[0], parts[1], parts[2], 0, 0, 0).toISOString();
};

const parseIsoStringFromCarbonToDate = (carbonDate) => {
  const parts = getDateTimePartsFromRawCarbonDateTime(carbonDate);
  return new Date(parts[0], parts[1], parts[2], 23, 59, 59).toISOString();
};

const convertToCarbonDateString = (dateTime) => {
  return `${dateTime.getFullYear()}${carbonDateSeparator}${formatCarbonDatePart(
    dateTime.getMonth() + 1
  )}${carbonDateSeparator}${formatCarbonDatePart(dateTime.getDate())}`;
};

const formatCarbonDatePart = (value) => {
  var zeroPrefixed = `0${value}`;
  return zeroPrefixed.substr(zeroPrefixed.length - 2);
};

const parseCarbonDateRangeFromISOStrings = (
  dateFromISOString,
  dateToISOString
) => {
  try {
    const dateFrom = parseDateFromISOString(dateFromISOString, new Date());
    const dateTo = parseDateFromISOString(dateToISOString, new Date());
    return [
      convertToCarbonDateString(dateFrom),
      convertToCarbonDateString(dateTo),
    ];
  } catch (error) {
    throw new Error(
      `UtcDateRange: Failed to parse date range: '${dateFromISOString}', '${dateToISOString}'`
    );
  }
};

const parseDateFromISOString = (isoDateString, fallBackValue) => {
  try {
    if (!isValidISODateString(isoDateString)) {
      throw new Error("Invalid ISO Date String!");
    }
    return new Date(isoDateString);
  } catch (error) {
    console.log(
      `UtcDateRange: Failed to parse isoDateString: '${isoDateString}', falling back to '${fallBackValue.toISOString()}'`
    );
  }
  return fallBackValue;
};

const UtcDateRange = ({
  idPrefix,
  fromDateUtc,
  toDateUtc,
  onChange,
  fromLabel,
  toLabel,
  inlineLabels,
  className,
}) => {
  const [carbonDateRange, setCarbonDateRange] = useState(
    parseCarbonDateRangeFromISOStrings(fromDateUtc, toDateUtc)
  );

  const sanitizedIdPrefix =
    idPrefix && idPrefix.length > 0 ? idPrefix : "utcDateRange";

  const startDateProps = {
    id: `${sanitizedIdPrefix}_fromDate`,
  };

  const endDateProps = {
    id: `${sanitizedIdPrefix}_toDate`,
  };

  useEffect(() => {
    setCarbonDateRange(
      parseCarbonDateRangeFromISOStrings(fromDateUtc, toDateUtc)
    );
  }, [fromDateUtc, toDateUtc]);

  const onRangeChanged = (range) => {
    const fromIsoDate = parseIsoStringFromCarbonFromDate(range[0].rawValue);
    const toIsoDate = parseIsoStringFromCarbonToDate(range[1].rawValue);
    onChange(fromIsoDate, toIsoDate);
  };

  return (
    <div className={className}>
      <DateRange
        value={carbonDateRange}
        onChange={(e) => onRangeChanged(e.target.value)}
        startDateProps={startDateProps}
        endDateProps={endDateProps}
        {...(fromLabel ? { startLabel: fromLabel } : {})}
        {...(toLabel ? { endLabel: toLabel } : {})}
        {...(inlineLabels ? { labelsInline: inlineLabels } : {})}
      />
    </div>
  );
};

export default UtcDateRange;
